import type { Permission, PermissionAction, PermissionSubject } from '../types/permissions.types';

export interface ParsedAbilities {
	can: (action: PermissionAction, subject: PermissionSubject) => boolean;
	cannot: (action: PermissionAction, subject: PermissionSubject) => boolean;
}

export interface RoutePermissions {
	canAccessDashboard: boolean;
	canAccessProps: boolean;
	canAccessSamples: boolean;
	canAccessRecommendations: boolean;
	canAccessProducts: boolean;
	canAccessParcelling: boolean;
	canAccessSupport: boolean;
	canAccessUsers: boolean;
}

export interface ForBindingRoutePermissions {
	canAccessFarmerProps: boolean;
	canWriteFarmerProps: boolean;
	canAccessFarmerSamples: boolean;
	canWriteFarmerSamples: boolean;
	canAccessFarmerRecommendations: boolean;
	canWriteFarmerRecommendations: boolean;
	canAccessFarmerUsers: boolean;
}

export interface BindingUsersPermissionsFromAbility {
	canRead: boolean;
	canCreate: boolean;
	canEdit: boolean;
	canDelete: boolean;
	canEnterProfile: boolean;
	canManageFarmers: boolean;
	canManageConsultants: boolean;
}

export const parsePermissions = (permissions: Permission[]): ParsedAbilities => {
	const allowed = new Set<string>();
	const denied = new Set<string>();

	permissions.forEach(({ action, subject, inverted }) => {
		const key = `${action}:${subject}`;
		if (inverted) denied.add(key);
		else allowed.add(key);
	});

	const can = (action: PermissionAction, subject: PermissionSubject): boolean => {
		const key = `${action}:${subject}`;
		if (denied.has(key)) return false;
		if (allowed.has('manage:all')) return true;
		if (allowed.has(`manage:${subject}`)) return true;
		if (allowed.has(`${action}:all`)) return true;
		return allowed.has(key);
	};

	const cannot = (action: PermissionAction, subject: PermissionSubject): boolean =>
		!can(action, subject);

	return { can, cannot };
};

export const buildBindingUsersPermissions = (
	abilities: ParsedAbilities,
	subject: 'farmers' | 'consultants'
): Pick<
	BindingUsersPermissionsFromAbility,
	'canRead' | 'canCreate' | 'canEdit' | 'canDelete' | 'canEnterProfile'
> => ({
	canRead: abilities.can('read', subject),
	canCreate: abilities.can('create', subject),
	canEdit: abilities.can('update', subject),
	canDelete: abilities.can('delete', subject),
	canEnterProfile: abilities.can('read', subject),
});

export const deriveRoutePermissions = (abilities: ParsedAbilities): RoutePermissions => ({
	canAccessDashboard: abilities.can('read', 'dashboard'),
	canAccessProps: abilities.can('read', 'props'),
	canAccessSamples: abilities.can('read', 'samples'),
	canAccessRecommendations: abilities.can('read', 'recommendations'),
	canAccessProducts: abilities.can('read', 'products'),
	canAccessParcelling: abilities.can('read', 'parcelling'),
	canAccessSupport: abilities.can('read', 'technical-support'),
	canAccessUsers: abilities.can('read', 'farmers') || abilities.can('read', 'consultants'),
});

export const deriveForBindingRoutePermissions = (
	abilities: ParsedAbilities
): ForBindingRoutePermissions => ({
	canAccessFarmerProps: abilities.can('read', 'farmers-props'),
	canWriteFarmerProps: abilities.can('create', 'farmers-props'),
	canAccessFarmerSamples: abilities.can('read', 'farmers-samples'),
	canWriteFarmerSamples: abilities.can('create', 'farmers-samples'),
	canAccessFarmerRecommendations: abilities.can('read', 'farmers-recommendations'),
	canWriteFarmerRecommendations: abilities.can('create', 'farmers-recommendations'),
	canAccessFarmerUsers: abilities.can('read', 'farmers'),
});

export const deriveBindingUsersPermissions = (
	abilities: ParsedAbilities
): BindingUsersPermissionsFromAbility => {
	const farmers = buildBindingUsersPermissions(abilities, 'farmers');
	const consultants = buildBindingUsersPermissions(abilities, 'consultants');

	return {
		canRead: farmers.canRead || consultants.canRead,
		canCreate: farmers.canCreate || consultants.canCreate,
		canEdit: farmers.canEdit || consultants.canEdit,
		canDelete: farmers.canDelete || consultants.canDelete,
		canEnterProfile: farmers.canEnterProfile || consultants.canEnterProfile,
		canManageFarmers: farmers.canRead,
		canManageConsultants: consultants.canRead,
	};
};
